import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import TitlePage from '../../components/TitlePage';
import H1Styled from '../../components/Typography/H1Styled';
import PrimaryButton from '../../components/Buttons/PrimaryButton';
import BaseModal from '../../components/Modals/BaseModal';
import FidelityForm from '../../components/Fidelity/FidelityForm';
import FidelityContractSection from '../../components/Fidelity/FidelityContractSection';

import { useAuth } from '../../contexts/Auth';
import {
  createFidelity,
  findFidelityByUserId,
  registerFidelityAccount,
  updateFidelity,
  validateFidelityCard,
} from '../../api/fidelity';
import { FidelityRequest, FidelityStatus } from '../../interfaces/fidelity';
import { fidelityValidator } from '../../validators/fidelity';
import useYupValidationResolver from '../../libs/YupValidationResolver';
import { isDelegato } from '../../libs/utils/auth';
import {
  COORDINATES_NOT_FOUND_WARNING,
  withResolvedCoordinates,
} from '../../libs/utils/geocoding';

const NuovoFidelity = () => {
  const navigate = useNavigate();
  const [{ token, profile }] = useAuth();
  const [isSavingAsDraft, setIsSavingAsDraft] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCredentialsModalOpen, setIsCredentialsModalOpen] = useState(false);
  const [createdEmail, setCreatedEmail] = useState('');
  const [createdUsername, setCreatedUsername] = useState('');
  const [createdPassword, setCreatedPassword] = useState('');

  const defaultValues: Partial<FidelityRequest> = {
    nome: '',
    ragioneSociale: '',
    partitaIva: '',
    telefono: '',
    telefonoSecondario: '',
    email: '',
    sito: '',
    indirizzo: '',
    cap: '',
    citta: '',
    provincia: '',
    descrizione: '',
    guidaProsit: false,
    note: '',
    categorie: [
      {
        categoria: '',
        sottocategoria: '',
        scontoOnline: 0,
        scontoNegozio: 0,
      },
    ],
    socioOnav: false,
    numeroTessera: undefined,
    sezione: isDelegato(profile) ? profile?.section?.data?.id : undefined,
    stato: FidelityStatus.BOZZA,
    scadenzaContratto: new Date().toISOString(),
    latitude: null,
    longitude: null,
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useForm<FidelityRequest>({
    resolver: useYupValidationResolver(fidelityValidator),
    defaultValues,
  });

  const generatePassword = useMemo(() => {
    return () =>
      Math.random().toString(36).slice(-8) +
      Math.random().toString(36).slice(-8).toUpperCase();
  }, []);

  const { mutate: createFlow, isLoading } = useMutation(
    'createFidelityFlow',
    async (formData: FidelityRequest) => {
      const password = generatePassword();
      const { data, coordinates } = await withResolvedCoordinates(formData);

      const registration = await registerFidelityAccount({
        email: data.email,
        password,
        piva: data.partitaIva,
      });

      if (!registration?.user?.id) {
        throw new Error('Creazione utente Fidelity non riuscita');
      }

      const userId = registration.user.id;

      const list = await findFidelityByUserId({ userId, token });
      const stato = isSavingAsDraft
        ? FidelityStatus.BOZZA
        : FidelityStatus.APPROVATO;

      if (list.data.length > 0) {
        const fid = list.data[0];
        let socioOnav = false;
        if (data.numeroTessera) {
          const res = await validateFidelityCard({
            token,
            numeroTessera: Number(data.numeroTessera),
          });
          if (!res.valid) {
            throw new Error(res.message || 'Numero tessera non valido');
          }
          socioOnav = res.socioOnav;
        }
        await updateFidelity({
          id: fid.id,
          body: { ...data, user: userId, stato, socioOnav },
          token,
        });
      } else {
        let socioOnav = false;
        if (data.numeroTessera) {
          const res = await validateFidelityCard({
            token,
            numeroTessera: Number(data.numeroTessera),
          });
          if (!res.valid) {
            throw new Error(res.message || 'Numero tessera non valido');
          }
          socioOnav = res.socioOnav;
        }
        await createFidelity({
          body: { ...data, user: userId, stato, socioOnav },
          token,
        });
      }

      setCreatedEmail(data.email);
      // username come salvato dal backend (P.IVA senza prefisso IT)
      setCreatedUsername(registration.user.username || data.partitaIva);
      setCreatedPassword(password);
      return { coordinates };
    },
    {
      onSuccess: ({ coordinates }) => {
        toast.success('Account Fidelity creato con successo');
        if (coordinates === 'not-found') {
          toast.warning(COORDINATES_NOT_FOUND_WARNING);
        }
        setIsCredentialsModalOpen(true);
      },
      onError: (e: any) => {
        const apiError = e?.response?.data?.error;
        const msg =
          apiError?.message ||
          apiError ||
          e?.message ||
          'Errore durante la creazione del Fidelity';
        toast.error(msg);
      },
    }
  );

  const onSubmit = (data: FidelityRequest) => {
    createFlow(data);
  };

  const ActionButtons = () => (
    <div className='flex gap-4'>
      <PrimaryButton outline onClick={() => setIsCancelModalOpen(true)}>
        Annulla
      </PrimaryButton>
      <PrimaryButton
        type='button'
        onClick={() => {
          setIsSavingAsDraft(true);
          handleSubmit(onSubmit)();
        }}
        isLoading={isLoading && isSavingAsDraft}
        disabled={isLoading}
      >
        Salva in bozza
      </PrimaryButton>
      <PrimaryButton
        type='button'
        onClick={() => {
          setIsSavingAsDraft(false);
          handleSubmit(onSubmit)();
        }}
        isLoading={isLoading && !isSavingAsDraft}
        disabled={isLoading}
      >
        Salva e approva
      </PrimaryButton>
    </div>
  );

  return (
    <>
      <TitlePage title='Nuovo Fidelity' />

      <div className='flex justify-between items-center mb-6'>
        <H1Styled>Nuovo Fidelity</H1Styled>
        <ActionButtons />
      </div>

      <FidelityContractSection
        fidelity={null}
        isCreateMode={true}
      />

      <form onSubmit={handleSubmit(onSubmit)} className='space-y-8'>
        <FidelityForm
          register={register}
          control={control}
          errors={errors}
          watch={watch}
          setValue={setValue}
          token={token!}
          profile={profile}
        />
        <div className='flex justify-end pt-4'>
          <ActionButtons />
        </div>
      </form>

      <BaseModal
        isOpen={isCancelModalOpen}
        toggle={() => setIsCancelModalOpen(false)}
        title='Conferma annullamento'
        subtitle='Sei sicuro di voler annullare? Tutti i dati inseriti andranno persi.'
        confirmButtonText='Sì, annulla'
        cancelButtonText='Continua a compilare'
        onConfirm={() => navigate('/fidelity')}
        onCancel={() => setIsCancelModalOpen(false)}
      />

      <BaseModal
        isOpen={isCredentialsModalOpen}
        toggle={() => setIsCredentialsModalOpen(false)}
        title='Credenziali account Fidelity'
        confirmButtonText='Chiudi'
        cancelButtonText=''
        onConfirm={() => {
          setIsCredentialsModalOpen(false);
          navigate('/fidelity');
        }}
        onCancel={() => {}}
      >
        <div className='mt-4'>
          <p>
            Prima di poter accedere a questo profilo Fidelity è necessario
            confermare l'email tramite il link inviato a{' '}
            <span className='font-medium break-all'>{createdEmail}</span>
          </p>
          <p className='text-sm text-gray-700'>
            Username:{' '}
            <span className='font-medium break-all'>{createdUsername}</span>
          </p>
          <p className='text-sm text-gray-700'>
            Password:{' '}
            <span className='font-medium break-all'>{createdPassword}</span>
          </p>
        </div>
      </BaseModal>
    </>
  );
};

export default NuovoFidelity;
